import React from 'react';

/**
 * Accessibility utility functions for the Risk Game application
 */

/**
 * Announces a message to screen readers using ARIA live regions
 * @param message - The message to announce
 * @param priority - The priority level ('polite' or 'assertive')
 * @param clearAfter - Time in milliseconds to clear the announcement (default: 3000)
 */
export const announce = (
  message: string,
  priority: 'polite' | 'assertive' = 'polite',
  clearAfter: number = 3000
): void => {
  const announcement = document.createElement('div');
  announcement.setAttribute('role', 'status');
  announcement.setAttribute('aria-live', priority);
  announcement.setAttribute('aria-atomic', 'true');
  announcement.style.position = 'absolute';
  announcement.style.left = '-10000px';
  announcement.style.width = '1px';
  announcement.style.height = '1px';
  announcement.style.overflow = 'hidden';
  
  announcement.textContent = message;
  document.body.appendChild(announcement);
  
  // Clear the announcement after specified time
  setTimeout(() => {
    document.body.removeChild(announcement);
  }, clearAfter);
};

/**
 * Creates a focus trap within a specified element
 * @param container - The container element to trap focus within
 * @returns Object with activate and deactivate methods
 */
export const createFocusTrap = (container: HTMLElement) => {
  let previouslyFocusedElement: HTMLElement | null = null;
  
  const focusableSelectors = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
  ].join(', ');
  
  const getFocusableElements = (): HTMLElement[] => {
    return Array.from(container.querySelectorAll(focusableSelectors));
  };
  
  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key !== 'Tab') return;
    
    const focusableElements = getFocusableElements();
    if (focusableElements.length === 0) return;
    
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];
    
    if (event.shiftKey && document.activeElement === firstElement) {
      event.preventDefault();
      lastElement.focus();
    } else if (!event.shiftKey && document.activeElement === lastElement) {
      event.preventDefault();
      firstElement.focus();
    }
  };
  
  return {
    activate: () => {
      previouslyFocusedElement = document.activeElement as HTMLElement;
      container.addEventListener('keydown', handleKeyDown);
      
      const focusableElements = getFocusableElements();
      if (focusableElements.length > 0) {
        focusableElements[0].focus();
      }
    },
    
    deactivate: () => {
      container.removeEventListener('keydown', handleKeyDown);
      if (previouslyFocusedElement) {
        previouslyFocusedElement.focus();
      }
    }
  };
};

/**
 * Keyboard navigation helper for managing arrow key navigation
 * @param items - Array of elements to navigate between
 * @param options - Navigation options
 */
export const createKeyboardNavigation = (
  items: HTMLElement[],
  options: {
    orientation?: 'horizontal' | 'vertical' | 'both';
    loop?: boolean;
    onSelect?: (element: HTMLElement, index: number) => void;
  } = {}
) => {
  const { orientation = 'both', loop = true, onSelect } = options;
  
  const handleKeyDown = (event: KeyboardEvent) => {
    const currentIndex = items.findIndex(item => item === document.activeElement);
    if (currentIndex === -1) return;
    
    let nextIndex = currentIndex;
    
    switch (event.key) {
      case 'ArrowUp':
        if (orientation === 'horizontal') return;
        event.preventDefault();
        nextIndex = currentIndex - 1;
        break;
      case 'ArrowDown':
        if (orientation === 'horizontal') return;
        event.preventDefault();
        nextIndex = currentIndex + 1;
        break;
      case 'ArrowLeft':
        if (orientation === 'vertical') return;
        event.preventDefault();
        nextIndex = currentIndex - 1;
        break;
      case 'ArrowRight':
        if (orientation === 'vertical') return;
        event.preventDefault();
        nextIndex = currentIndex + 1;
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        if (onSelect) {
          onSelect(items[currentIndex], currentIndex);
        }
        return;
      case 'Home':
        event.preventDefault();
        nextIndex = 0;
        break;
      case 'End':
        event.preventDefault();
        nextIndex = items.length - 1;
        break;
      default:
        return;
    }
    
    if (loop) {
      nextIndex = (nextIndex + items.length) % items.length;
    } else {
      nextIndex = Math.max(0, Math.min(nextIndex, items.length - 1));
    }
    
    items[nextIndex].focus();
  };
  
  return {
    activate: () => {
      items.forEach(item => {
        item.addEventListener('keydown', handleKeyDown);
        if (!item.hasAttribute('tabindex')) {
          item.setAttribute('tabindex', '0');
        }
      });
    },
    
    deactivate: () => {
      items.forEach(item => {
        item.removeEventListener('keydown', handleKeyDown);
      });
    }
  };
};

/**
 * Checks if the user prefers reduced motion
 * @returns true if the user prefers reduced motion
 */
export const prefersReducedMotion = (): boolean => {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
};

/**
 * Generates a unique ID for ARIA relationships
 * @param prefix - Optional prefix for the ID
 * @returns A unique ID string
 */
export const generateAriaId = (prefix: string = 'aria'): string => {
  return `${prefix}-${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Sets up skip navigation links
 * @param targetId - The ID of the main content area
 * @returns The skip link element
 */
export const createSkipLink = (targetId: string): HTMLAnchorElement => {
  const skipLink = document.createElement('a');
  skipLink.href = `#${targetId}`;
  skipLink.className = 'skip-link';
  skipLink.textContent = 'Skip to main content';
  
  skipLink.addEventListener('click', (event) => {
    event.preventDefault();
    const target = document.getElementById(targetId);
    if (target) {
      target.setAttribute('tabindex', '-1');
      target.focus();
      target.scrollIntoView();
    }
  });
  
  return skipLink;
};

/**
 * Manages focus for modals and overlays
 * @param isOpen - Whether the modal is open
 * @param modalElement - The modal element
 * @param triggerElement - The element that triggered the modal
 */
export const manageFocusForModal = (
  isOpen: boolean,
  modalElement: HTMLElement | null,
  triggerElement: HTMLElement | null
) => {
  if (isOpen && modalElement) {
    // Store current focus
    const previouslyFocused = document.activeElement as HTMLElement;
    
    // Focus first focusable element in modal
    const focusableElements = modalElement.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    
    if (focusableElements.length > 0) {
      (focusableElements[0] as HTMLElement).focus();
    } else {
      modalElement.focus();
    }
    
    // Return focus function
    return () => {
      if (previouslyFocused) {
        previouslyFocused.focus();
      } else if (triggerElement) {
        triggerElement.focus();
      }
    };
  }
  
  return null;
};

/**
 * Debounces screen reader announcements to prevent overwhelming users
 * @param announceFunc - The announce function to debounce
 * @param delay - Delay in milliseconds
 * @returns Debounced announce function
 */
export const debounceAnnounce = (
  announceFunc: typeof announce,
  delay: number = 1000
) => {
  let timeoutId: NodeJS.Timeout | null = null;
  
  return (message: string, priority?: 'polite' | 'assertive') => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    
    timeoutId = setTimeout(() => {
      announceFunc(message, priority);
    }, delay);
  };
};

/**
 * Helper to ensure proper heading hierarchy
 * @param level - The desired heading level
 * @returns The appropriate heading tag name
 */
export const getHeadingLevel = (level: number): keyof React.JSX.IntrinsicElements => {
  const clampedLevel = Math.max(1, Math.min(6, level));
  return `h${clampedLevel}` as keyof React.JSX.IntrinsicElements;
};